/**
 * Pure helpers behind the shared Doc Control folder tree (FolderTree.tsx).
 * Kept free of React so the expansion and keyboard rules can be unit-tested.
 */

export interface FolderTreeNode {
  id: string
  name: string
  documentCount?: number | null
  restricted?: boolean | null
  children?: FolderTreeNode[] | null
}

export interface VisibleFolderRow {
  node: FolderTreeNode
  level: number
  parentId: string | null
  hasChildren: boolean
  isExpanded: boolean
}

// Tailwind only generates classes it can find as literal strings, so the
// indentation is a fixed ladder rather than `pl-${n}`. DOC_FOLDER_TREE fetches
// four levels (top + three nested `children`); Photos folders nest six deep, so
// the ladder has six steps.
const INDENTS = ['pl-1', 'pl-6', 'pl-11', 'pl-16', 'pl-[5.25rem]', 'pl-[6.5rem]']

export function folderIndentClass(level: number): string {
  return INDENTS[Math.max(0, Math.min(level, INDENTS.length - 1))]
}

/** Rows currently on screen, depth-first, skipping the children of collapsed folders. */
export function visibleFolderRows(folders: FolderTreeNode[], expanded: Set<string>): VisibleFolderRow[] {
  const rows: VisibleFolderRow[] = []
  const visit = (nodes: FolderTreeNode[], level: number, parentId: string | null) => {
    nodes.forEach((node) => {
      const hasChildren = !!node.children && node.children.length > 0
      const isExpanded = hasChildren && expanded.has(node.id)
      rows.push({ node, level, parentId, hasChildren, isExpanded })
      if (isExpanded) visit(node.children, level + 1, node.id)
    })
  }
  visit(folders, 0, null)
  return rows
}

export function toggleExpanded(expanded: Set<string>, id: string): Set<string> {
  const next = new Set(expanded)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return next
}

/** `expanded` plus `ids`; the same instance when nothing is new, so callers can skip a state update. */
export function expandedWith(expanded: Set<string>, ids: string[]): Set<string> {
  const missing = ids.filter((id) => !expanded.has(id))
  if (missing.length === 0) return expanded
  const next = new Set(expanded)
  missing.forEach((id) => next.add(id))
  return next
}

/** Ids of the folders above `id`, outermost first; null when `id` is not in the tree. */
export function folderAncestorIds(folders: FolderTreeNode[], id: string): string[] | null {
  const walk = (nodes: FolderTreeNode[], path: string[]): string[] | null => {
    for (const node of nodes) {
      if (node.id === id) return path
      if (node.children && node.children.length > 0) {
        const found = walk(node.children, path.concat(node.id))
        if (found) return found
      }
    }
    return null
  }
  return walk(folders, [])
}

export type FolderTreeKeyAction =
  | { type: 'focus'; id: string }
  | { type: 'expand'; id: string }
  | { type: 'collapse'; id: string }

/**
 * What a navigation key does on the focused row (WAI-ARIA tree pattern):
 * Up/Down/Home/End move focus; Right expands a closed folder or steps into an
 * open one; Left collapses an open folder or steps out to its parent.
 */
export function folderTreeKeyAction(rows: VisibleFolderRow[], currentId: string | null, key: string): FolderTreeKeyAction | null {
  if (rows.length === 0) return null
  const index = rows.findIndex((row) => row.node.id === currentId)
  if (index === -1) return { type: 'focus', id: rows[0].node.id }
  const row = rows[index]
  switch (key) {
    case 'ArrowDown':
      return index < rows.length - 1 ? { type: 'focus', id: rows[index + 1].node.id } : null
    case 'ArrowUp':
      return index > 0 ? { type: 'focus', id: rows[index - 1].node.id } : null
    case 'Home':
      return index > 0 ? { type: 'focus', id: rows[0].node.id } : null
    case 'End':
      return index < rows.length - 1 ? { type: 'focus', id: rows[rows.length - 1].node.id } : null
    case 'ArrowRight':
      if (!row.hasChildren) return null
      return row.isExpanded ? { type: 'focus', id: rows[index + 1].node.id } : { type: 'expand', id: row.node.id }
    case 'ArrowLeft':
      if (row.isExpanded) return { type: 'collapse', id: row.node.id }
      return row.parentId ? { type: 'focus', id: row.parentId } : null
    default:
      return null
  }
}
